import React from 'react';
import loadable from '@loadable/component';
import { Provider } from 'react-redux';
import { Route } from 'react-router-dom';
import { fireLanding } from '../modules/analytics/landing';
import { IRouteState } from '../../../packages/router';
import store from '../store';
import AppInstallBanner from './shared/AppInstallBanner';
import StateRouterConnector from './shared/StateRouterConnector';
import { routeConfigs } from '../react-routes';
import ForceTOSAccept from './shared/legal/ForceTOSAccept';
import ConnectedSnackbar from './ui/Snackbar';
import * as CJTracking from '../modules/CJTracking/CJTracking';
import { StripeTerminalConnector } from './provider/StripeTerminal/StripeTerminalConnector';

const AngularApp = loadable(() => import('./AngularApp'));

/**
 * Main React App
 * @constructor
 */
const App = () => {
  /*
   * When the user goes to a new route run navigational analytics.
   */
  const handleNavigationAnalytics = (route: IRouteState) => {
    // Determine where the user was.
    const previousLocationPath: string | null = window.document.referrer
      ? window.document.referrer
      // The application was navigated "manually" by the user through the browser's URL.
      : null;
    const previousLocationPathIsFromReload = (() => {
      // https://caniuse.com/mdn-api_performancenavigationtiming
      if (window.performance && window.performance.getEntriesByType) {
        return (
          window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
        )?.type === 'reload';
      }
      return false;
    })();

    // When the user is coming from a different site/page for the *first* time.
    //
    // Using direct access to `window.location.href` because there are currently two routers
    // in this application.
    if (!previousLocationPathIsFromReload) {
      // NOTE: I think we should be tracking every navigational change from any site/page.
      //       But that would mean changing the definition of a "landing event" for the data team.
      //
      //       This whitelist conditional is how we are currently tracking landing events.
      //
      // The landing event is triggered under the following conditions:
      if (
        // The user navigated to our application for the first time ever.
        previousLocationPath === null
        // The user navigated from an external site.
        || previousLocationPath.indexOf(window.location.origin) <= -1
        // The user navigated from a whitelisted internal page.
        || previousLocationPath.indexOf(`${window.location.origin}/blog/`) >= 0
        || previousLocationPath.indexOf(`${window.location.origin}/promo/`) >= 0
      ) {
        fireLanding(route);
      }
    }

    if (route.params?.TYPE) {
      CJTracking.storeParams({
        CJEVENT: route.params.CJEVENT,
        TYPE: route.params.TYPE,
      });
    }
  };

  return (
    <Provider store={store}>
      <AppInstallBanner />
      <StateRouterConnector
        routes={routeConfigs}
        onNavigation={handleNavigationAnalytics}
      >
        {/* when none of the route configs can handle a route state, render the angular app */}
        <Route>
          <AngularApp />
        </Route>
      </StateRouterConnector>
      <ForceTOSAccept />
      <ConnectedSnackbar />
      <StripeTerminalConnector />
    </Provider>
  );
};

export default App;
